/**
 * Keys whose values must be redacted before structured logging on the web
 * side. Mirrors `apps/bff/src/common/redact.ts` SENSITIVE_KEYS by convention —
 * cross-app sharing isn't trivial in this monorepo, so the two lists stay
 * in sync manually. If you change one, change both.
 *
 * The same rationale as the BFF list:
 *   - REDACT: credentials, one-time codes, payment card data, bank identifiers,
 *     identity documents, address lines.
 *   - KEEP visible: names, emails, phones (used for support correlation).
 */
export const SENSITIVE_KEYS_WEB: ReadonlySet<string> = new Set(
  [
    // credentials
    "password",
    "passcode",
    "passphrase",
    "secret",
    "token",
    "accesstoken",
    "refreshtoken",
    "apikey",
    "authorization",
    "cookie",
    // one-time codes
    "otp",
    "otop",
    "onetimecode",
    "verificationcode",
    "mfacode",
    // payment card data
    "cardnumber",
    "pan",
    "cvv",
    "cvc",
    "cardcvv",
    // bank identifiers
    "iban",
    "accountnumber",
    "bankaccountnumber",
    "sortcode",
    "routingnumber",
    "bicswift",
    "bic",
    // identity documents
    "dateofbirth",
    "dob",
    "birthdate",
    "nationality",
    "nationalid",
    "taxid",
    "taxidentificationnumber",
    "passportnumber",
    "driverslicensenumber",
    // address
    "addressline1",
    "addressline2",
    "postcode",
    "zipcode",
    // upstream ExceptionDetail instance data. The BFF forwards the microservice
    // error body verbatim (errors.interceptor.ts spreads `err.response.data`),
    // and `parameters` is specified as a free-form `Record<string, string>` of
    // per-instance values — i.e. whatever the customer typed. `message`
    // (description of the exception TYPE) and `messages` (description of the
    // exception INSTANCE) stay visible because they are the only readable
    // account of what upstream refused; the embedded value inside them is
    // scrubbed separately by `stripEmbeddedValues` below.
    "parameters",
  ].map((k) => k.toLowerCase())
);

/**
 * Bean Validation on the `@jftechnology/*` services reports a rejected
 * attribute by appending the offending value to the message:
 *
 *   [Operation 'createBeneficiary'] Parameter 'arg0' Attribute
 *   'bankAccount.bic' must match "^[A-Z]{6}…$" (value=REVOLT21 )
 *
 * Everything before `(value=` is schema — the operation, the attribute and the
 * pattern — and is exactly what makes an error triageable. The `(value=…)` tail
 * is the customer's own input: a BIC, an IBAN, an account number, a name.
 *
 * Key-based redaction cannot reach it (it lives inside a string, under the
 * non-sensitive key `messages`), so it is stripped by shape instead. Applied to
 * every string that passes through `redact()`, which is what `lib/logError.ts`
 * runs over an RTK Query error body before it reaches the Mixpanel replay
 * console, the OTel/Kibana log line and the Sentry breadcrumb trail.
 *
 * Mirrors `stripEmbeddedValues` in `apps/bff/src/common/redact.ts`.
 *
 * CUSTOMER-UI-3Q is the worked example: the production event carried
 * `(value=REVOLT21 )` — a real customer's BIC, trailing space and all.
 */
const EMBEDDED_VALUE = /\(value=[^)]*\)/gi;

export function stripEmbeddedValues(text: string): string {
  return text.replace(EMBEDDED_VALUE, "(value=[REDACTED])");
}
